/**
 * The bare-noun rung (D133): the WHOLE clause names a thing, so the clause is
 * a look at that thing — resolved before any narrower reading is allowed to
 * commit.
 *
 * @remarks
 * The defect this closes (D123 §2.1, "the engine committing before it
 * finishes reading"): the unknown-verb rung reads token 0 as a verb and
 * offers only the REMAINDER to resolution, so `locker eleven` became "the
 * action LOCKER, applied to locker eleven" whenever the remainder happened to
 * resolve alone. The runtime patched the proposal after the fact — it
 * re-parsed the text as `inspect …` and replaced the diagnosis — but the
 * patch could not follow every path the diagnosis took (the which-entity
 * answer path never saw it, and a pack with no `inspect` intent had no
 * reading at all). The fix belongs where the reading is made: one rung that
 * tries the WIDEST reading first.
 *
 * The rung is the same resolver the grammar uses for an object phrase
 * (`resolveEntityPhrase`: longest head, adjectives filter and consume,
 * salience and the clarification window), applied to the clause entire. So
 * a word the catalog cannot place as an adjective still eliminates every
 * candidate, and `gribble the kettle` keeps the unknown-verb teach it earned
 * (D36) — the rung declines and the ladder continues. A TIE is a
 * which-entity clarification asked HERE, in the parser, where the ambiguity
 * is visible, rather than a diagnosis for the runtime to re-litigate. A
 * pronoun alone resolves against recency exactly as it would after a verb.
 *
 * Shape routing invents nothing: the vocabulary must declare `inspect`, and
 * the compiled intent rides on the result so no consumer re-looks-it-up.
 *
 * @packageDocumentation
 */

import { type CanonicalToken } from '../grammar/canonicalize-tokens.js';
import { type PhraseToken } from '../grammar/match-clause-structure.js';
import { type CompiledIntent, type CompiledLexicon } from '../lexicon/compile-lexicon.js';
import { type ResolvedEntityPhrase, resolveEntityPhrase } from '../resolve/resolve-entity-phrase.js';
import { type ClarificationRequest, type EntityRef, type ParseCandidateEntity } from '../types.js';

/** The intent a bare noun is read as (D37): naming a thing means looking at it. */
export const BARE_NOUN_INTENT_TOKEN = 'inspect';

/**
 * Where the rung is standing when it reads.
 *
 * - `after_the_grammar` (the ladder): a clause holding a verb or a bearing
 *   is not a bare noun — it failed the grammar for a structural reason the
 *   diagnostics already explain honestly (a verb with no object, a second
 *   verb, an unplaced preposition) — so the rung declines it.
 * - `before_the_grammar` (an answer slot, `readAsNounPhrase`): the question
 *   already supplied the verb, so every token is NAME material whatever the
 *   vocabulary would call it — "pry bar" is the bar (D64), and `pry` being
 *   an intent is beside the point.
 */
export type BareNounReading = 'after_the_grammar' | 'before_the_grammar';

/** What the rung produced: a look at one thing, or the question of which. */
export type BareNounRescueResult =
	| { readonly clarification: ClarificationRequest; readonly type: 'clarification' }
	| { readonly routed: RoutedBareNoun; readonly type: 'routed_bare_noun' };

/** A bare noun routed onto the inspect intent — the orchestrator assembles the command. */
export interface RoutedBareNoun {
	/** The vocabulary's own `inspect` record, resolved before the rung agreed to route. */
	readonly intent: CompiledIntent;
	/** The phrase's resolution — ref, consumed adjectives, and the pronoun evidence when the head was one. */
	readonly resolution: ResolvedEntityPhrase;
}

/**
 * Try the whole clause as one noun phrase.
 *
 * @param canonicalTokens - The clause's canonical tokens (fuzzy-corrected where the caller had them)
 * @param clauseText - The clause, folded (clarifications cite it)
 * @param lexicon - The compiled vocabulary (must declare `inspect` for the rung to speak)
 * @param entities - The per-parse catalog
 * @param recentEntityRefs - Most-recent-first refs, for a pronoun head
 * @param reading - See {@link BareNounReading}
 * @returns The routed look, the which-entity question, or `undefined` when the clause is not one thing's name
 */
export const rescueBareNoun = (
	canonicalTokens: readonly CanonicalToken[],
	clauseText: string,
	lexicon: CompiledLexicon,
	entities: readonly ParseCandidateEntity[],
	recentEntityRefs: readonly EntityRef[],
	reading: BareNounReading = 'after_the_grammar',
): BareNounRescueResult | undefined => {
	const inspectIntent = lexicon.intentsByToken.get(BARE_NOUN_INTENT_TOKEN);
	const phraseTokens = phraseTokensOf(canonicalTokens, reading);

	if (inspectIntent === undefined || phraseTokens === undefined) {
		return undefined;
	}

	const phraseOutcome = resolveEntityPhrase(
		phraseTokens,
		withoutPageGrammar(entities, canonicalTokens, lexicon),
		recentEntityRefs,
	);

	if (phraseOutcome.type === 'not_found') {
		return undefined;
	}

	if (phraseOutcome.type === 'needs_clarification') {
		return {
			clarification: { candidates: phraseOutcome.candidates, clauseText, kind: 'which_entity' },
			type: 'clarification',
		};
	}

	return { routed: { intent: inspectIntent, resolution: phraseOutcome.resolution }, type: 'routed_bare_noun' };
};

/**
 * The catalog with the clause's own VERB words struck from every page (D230),
 * and its prepositions (D245) — the clause's grammar, which a page may not
 * lend back to it as a name.
 *
 * @remarks
 * Before the grammar, every token is name material — "pry bar" is the bar,
 * and `pry` being an intent is beside the point (D64). What makes that true
 * is that the pack DECLARED "pry bar": a name or an adjective the author
 * wrote is the thing's own word, in whatever role the vocabulary gives it
 * elsewhere. The page is different. D205 lets a thing's description lend it
 * modifiers ("the old man"), and a description is prose — it says what is
 * done at the thing. Salt Debt's well says "put", "coin", "on" and "leave",
 * so under "Where, or on what?" the command `put coin on well` read as a
 * noun phrase: the well, qualified by three words of its own paragraph, and
 * the player was told "You cannot leave the coin in put coin on well". The
 * canonical `use coin on well` ran, because no page says "use" — which is
 * what made the defect look like one about aliases.
 *
 * So in the answer slot a verb the player typed may be claimed by a thing's
 * declared words (its head, its adjectives) and never by its page alone:
 * the page's verbs are struck before resolution, and the phrase that needed
 * them falls through to the grammar, which reads the command the player
 * meant. Only the words the clause itself canonicalized as intents are
 * struck, so a page's other words keep every modifier D205 gave them.
 *
 * D245 (the Meridian play-test's A1): and so are the clause's PREPOSITIONS,
 * in both readings. The Meridian's couch is "bolted to the deck the way
 * everything is that people lie on out here", so `lie on the couch` read as
 * the couch qualified by two words of its own page — a look at the couch
 * when typed alone, and under "Use it on what?" the answer to the question
 * — and `lie` is no verb of the pack's, so the verb strike above never saw
 * it. A preposition is how a sentence binds a verb to its thing, never how a
 * name describes one; a page that says it is prose placing the thing, not a
 * modifier the player could mean. Struck, the phrase is no bare noun, and
 * the ladder reads the attempt (`lie` at the couch) the player typed. A
 * preposition inside a thing's DECLARED name (D242: `coin from home`) is
 * matched as the name, never through the page, and is untouched. After the
 * grammar a clause holds no intents (the rung declines it), so the verb
 * strike only ever acts before it.
 *
 * @param entities - The per-parse catalog
 * @param canonicalTokens - The clause's canonical tokens, the intents among them as typed
 * @param lexicon - The compiled vocabulary (its declared prepositions)
 * @returns The same catalog, each page without the clause's verb words and prepositions — or the catalog itself when the clause has neither
 */
const withoutPageGrammar = (
	entities: readonly ParseCandidateEntity[],
	canonicalTokens: readonly CanonicalToken[],
	lexicon: CompiledLexicon,
): readonly ParseCandidateEntity[] => {
	const typedVerbWords = new Set(
		canonicalTokens.flatMap((canonicalToken) => {
			if (canonicalToken.kind === 'intent') {
				return canonicalToken.sourceText.split(' ');
			}

			return lexicon.prepositionTokens.has(canonicalToken.sourceText) ? [canonicalToken.sourceText] : [];
		}),
	);

	if (typedVerbWords.size === 0) {
		return entities;
	}

	return entities.map((entity) =>
		entity.descriptionWords === undefined
			? entity
			: { ...entity, descriptionWords: entity.descriptionWords.filter((pageWord) => !typedVerbWords.has(pageWord)) },
	);
};

/**
 * The clause as noun-phrase material, per the reading — or `undefined` when
 * the reading says this clause cannot be a name.
 *
 * @param canonicalTokens - The clause's canonical tokens
 * @param reading - See {@link BareNounReading}
 * @returns The phrase tokens, or `undefined`
 */
const phraseTokensOf = (
	canonicalTokens: readonly CanonicalToken[],
	reading: BareNounReading,
): undefined | readonly PhraseToken[] => {
	if (canonicalTokens.length === 0) {
		return undefined;
	}

	if (reading === 'before_the_grammar') {
		return canonicalTokens.map((canonicalToken): PhraseToken =>
			canonicalToken.kind === 'pronoun'
				? canonicalToken
				: { kind: 'word', sourceSpan: canonicalToken.sourceSpan, sourceText: canonicalToken.sourceText },
		);
	}

	const phraseTokens = canonicalTokens.flatMap((canonicalToken): PhraseToken[] =>
		canonicalToken.kind === 'word' || canonicalToken.kind === 'pronoun' ? [canonicalToken] : [],
	);

	return phraseTokens.length === canonicalTokens.length ? phraseTokens : undefined;
};
